/**
 * WebSocket connection management for the Fyncall Widget
 * Handles real-time messaging with reconnection logic
 */

import { getConfig } from '../core/config.js';
import { getState, setState, getSession, getVisitorToken } from '../core/state.js';
import { getToken } from '../core/auth.js';

/**
 * WebSocket manager class
 */
class WebSocketManager {
    constructor() {
        this.websocket = null;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.consecutiveFailures = 0;
        this.sessionInvalid = false;
        this.messageHandlers = new Set();
        this.statusHandlers = new Set();
        this.reconnectTimer = null;
    }

    /**
     * Connect to WebSocket server
     * @param {Object} options - Connection options
     * @param {string|number} options.sessionId - Session ID
     * @param {string} options.visitorToken - Visitor token
     * @param {Function} options.onInvalidSession - Callback for invalid session
     * @returns {boolean} Whether connection was initiated
     */
    connect({ sessionId, visitorToken, onInvalidSession } = {}) {
        const session = sessionId || getSession();
        const token = visitorToken || getVisitorToken();

        if (!session || !token) {
            console.log('[FyncallWS] Missing session or token, cannot connect');
            return false;
        }

        if (this.sessionInvalid) {
            console.log('[FyncallWS] Session invalid, skipping connection');
            return false;
        }

        // Store callback
        this.onInvalidSession = onInvalidSession;

        try {
            const config = getConfig();
            const wsProtocol = config.baseUrl.startsWith('https') ? 'wss' : 'ws';
            const wsHost = config.baseUrl.replace(/^https?:\/\//, '');

            // Token-based auth only
            const jwtToken = getToken();
            if (!jwtToken) {
                console.error('[FyncallWS] No auth token available');
                return false;
            }

            const wsUrl = `${wsProtocol}://${wsHost}/api/v1/ws/widget?token=${encodeURIComponent(jwtToken)}&session_id=${session}&visitor_token=${token}`;

            console.log('[FyncallWS] Connecting:', wsUrl.replace(/token=[^&]+/, 'token=***'));
            this.websocket = new WebSocket(wsUrl);

            this.websocket.onopen = this._handleOpen.bind(this);
            this.websocket.onmessage = this._handleMessage.bind(this);
            this.websocket.onclose = this._handleClose.bind(this);
            this.websocket.onerror = this._handleError.bind(this);

            return true;
        } catch (error) {
            console.error('[FyncallWS] Connection error:', error);
            return false;
        }
    }

    /**
     * Disconnect WebSocket
     * @param {boolean} preventReconnect - Whether to prevent auto-reconnect
     */
    disconnect(preventReconnect = false) {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }

        if (this.websocket) {
            if (preventReconnect) {
                this.websocket.onclose = null;
            }
            this.websocket.close();
            this.websocket = null;
        }

        this._notifyStatus('disconnected');
    }

    /**
     * Check if WebSocket is connected
     * @returns {boolean} Connection status
     */
    isConnected() {
        return this.websocket && this.websocket.readyState === WebSocket.OPEN;
    }

    /**
     * Send a message through WebSocket
     * @param {Object} data - Data to send
     * @returns {boolean} Whether message was sent
     */
    send(data) {
        if (!this.isConnected()) {
            console.warn('[FyncallWS] Cannot send, not connected');
            return false;
        }

        try {
            this.websocket.send(JSON.stringify(data));
            return true;
        } catch (error) {
            console.error('[FyncallWS] Send error:', error);
            return false;
        }
    }

    /**
     * Subscribe to incoming messages
     * @param {Function} handler - Message handler function
     * @returns {Function} Unsubscribe function
     */
    onMessage(handler) {
        this.messageHandlers.add(handler);
        return () => this.messageHandlers.delete(handler);
    }

    /**
     * Subscribe to connection status changes
     * @param {Function} handler - Status handler function
     * @returns {Function} Unsubscribe function
     */
    onStatus(handler) {
        this.statusHandlers.add(handler);
        return () => this.statusHandlers.delete(handler);
    }

    /**
     * Mark session as invalid (prevents reconnection)
     */
    markSessionInvalid() {
        this.sessionInvalid = true;
        this.disconnect(true);
    }

    /**
     * Reset session invalid state
     */
    resetSessionState() {
        this.sessionInvalid = false;
        this.reconnectAttempts = 0;
        this.consecutiveFailures = 0;
    }

    // Private methods

    _handleOpen() {
        console.log('[FyncallWS] Connected');
        this.reconnectAttempts = 0;
        this.consecutiveFailures = 0;
        this._notifyStatus('connected');
        setState('websocketConnected', true);
    }

    _handleMessage(event) {
        try {
            const data = JSON.parse(event.data);

            // Check for session invalid message from server
            if (data.type === 'error' && (data.code === 'SESSION_INVALID' || data.code === 'SESSION_NOT_FOUND')) {
                console.log('[FyncallWS] Server reported invalid session');
                this._handleInvalidSession();
                return;
            }

            console.log('[FyncallWS] Message:', data);

            // Notify all handlers
            this.messageHandlers.forEach(handler => {
                try {
                    handler(data);
                } catch (e) {
                    console.error('[FyncallWS] Handler error:', e);
                }
            });
        } catch (e) {
            console.error('[FyncallWS] Parse error:', e);
        }
    }

    _handleClose(event) {
        console.log('[FyncallWS] Disconnected, code:', event.code);
        setState('websocketConnected', false);
        this._notifyStatus('disconnected');

        // Code 1008 = Policy Violation (often used for invalid session)
        if (event.code === 1008) {
            this.consecutiveFailures++;
            if (this.consecutiveFailures >= 3) {
                console.log('[FyncallWS] Multiple failures, session likely invalid');
                this._handleInvalidSession();
                return;
            }
        }

        this._attemptReconnect();
    }

    _handleError(error) {
        console.error('[FyncallWS] Error:', error);
        this.consecutiveFailures++;
    }

    _attemptReconnect() {
        if (this.sessionInvalid) {
            console.log('[FyncallWS] Session invalid, not reconnecting');
            return;
        }

        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.log('[FyncallWS] Max reconnect attempts reached');
            this._notifyStatus('max_retries');
            return;
        }

        this.reconnectAttempts++;
        const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
        console.log(`[FyncallWS] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);

        this._notifyStatus('reconnecting', { attempt: this.reconnectAttempts, delay });

        this.reconnectTimer = setTimeout(() => {
            this.connect();
        }, delay);
    }

    _handleInvalidSession() {
        this.sessionInvalid = true;

        if (this.websocket) {
            this.websocket.onclose = null;
            this.websocket.close();
            this.websocket = null;
        }

        this._notifyStatus('session_invalid');

        if (this.onInvalidSession) {
            this.onInvalidSession();
        }
    }

    _notifyStatus(status, data = {}) {
        this.statusHandlers.forEach(handler => {
            try {
                handler(status, data);
            } catch (e) {
                console.error('[FyncallWS] Status handler error:', e);
            }
        });
    }
}

// Export singleton instance
export const websocketManager = new WebSocketManager();

// Export convenience functions

/**
 * Connect to WebSocket
 * @param {Object} options - Connection options
 * @returns {boolean} Whether connection was initiated
 */
export function connectWebSocket(options) {
    return websocketManager.connect(options);
}

/**
 * Disconnect WebSocket
 * @param {boolean} preventReconnect - Prevent auto-reconnect
 */
export function disconnectWebSocket(preventReconnect = false) {
    websocketManager.disconnect(preventReconnect);
}

/**
 * Check if WebSocket is connected
 * @returns {boolean} Connection status
 */
export function isWebSocketConnected() {
    return websocketManager.isConnected();
}

/**
 * Send message through WebSocket
 * @param {Object} data - Data to send
 * @returns {boolean} Whether sent successfully
 */
export function sendWebSocketMessage(data) {
    return websocketManager.send(data);
}

/**
 * Subscribe to WebSocket messages
 * @param {Function} handler - Message handler
 * @returns {Function} Unsubscribe function
 */
export function onWebSocketMessage(handler) {
    return websocketManager.onMessage(handler);
}

/**
 * Subscribe to WebSocket status changes
 * @param {Function} handler - Status handler
 * @returns {Function} Unsubscribe function
 */
export function onWebSocketStatus(handler) {
    return websocketManager.onStatus(handler);
}

/**
 * Mark session as invalid
 */
export function markWebSocketSessionInvalid() {
    websocketManager.markSessionInvalid();
}

/**
 * Reset WebSocket session state
 */
export function resetWebSocketSession() {
    websocketManager.resetSessionState();
}
